import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../../../Component/breadcrumb/Breadcrumb";
import "../styles/details.css";
import GalleryImage from "../component/GalleryImage";
import SpecifIcation from "../../../Component/Common/SpecifIcation";
import MobileFilterPdp from "../../../Component/Layout/MobileFilter/MobileFilterPdp";
import SearchFilter from "../../../Component/search/SearchFilter";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { getProductDetails } from "../service/productdetails.service";
import { getgridUrl,getGridActionLink } from "../../../utils/url.utils";
import noproduct from "../../../assets/images/Noproduct-Found.png";
import CatalogComponent from "../../../Component/Common/CatalogComponent";
import CartList from "../../Cart/CartList";


function ProductDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [products, setProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({
    design: [],
    shade: [],
    size: [],
    shape: [],
    price: "",
  });

  /* ============================
     GET PRODUCT DETAILS
  ============================ */

const includeComingSoon = Number(searchParams.get("includeComingSoon") || 0);
const source = searchParams.get("source") || 'header';


useEffect(() => {
  if (slug) {
    loadProduct(slug, source, includeComingSoon);
  }
}, [slug,source, includeComingSoon]);

const loadProduct = async (productSlug, source='header',includeComingSoon = 0) => {
  setLoading(true);

  try {
    const data = await getProductDetails(productSlug,source, includeComingSoon);

    const productData = Array.isArray(data) ? data : [];

    setProducts(productData);

    if (productData.length > 0) {
      setSelectedImage(productData[0].image_path);
      setSelectedProduct(productData[0]);
    } else {
      setSelectedImage("");
      setSelectedProduct(null);
    }
  } catch (error) {
    console.error("Product Details Error:", error);
    setProducts([]);
    setSelectedImage("");
    setSelectedProduct(null);
  } finally {
    setLoading(false);
  }
};

  /* ============================
     IMAGE CLICK
  ============================ */

  const handleImageClick = (item) => {
    setSelectedImage(item.image_path);
    setSelectedProduct(item);
  };

  /* ============================
     MOBILE
  ============================ */

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* ============================
     APPLY FILTER 
  ============================ */

  const handleApplyFilters = (filters) => {
    setSelectedFilters({

      design: Array.isArray(filters?.design)
        ? filters.design
        : [],

      shade: Array.isArray(filters?.shade)
        ? filters.shade
        : [],

      size: Array.isArray(filters?.size)
        ? filters.size
        : [],

      shape: Array.isArray(filters?.shape)
        ? filters.shape
        : [],

      price: filters?.price || "",

    });
  };

  /* ============================
     CLEAR FILTER
  ============================ */

  const handleClearFilters = () => {
    setSelectedFilters({
      design: [],
      shade: [],
      size: [],
      shape: [],
      price: "",
    });
  };

  /* ============================
     PRICE MATCH
  ============================ */

  const priceMatches = (mrp, priceFilter) => {
    const price = Number(mrp);
    if (Number.isNaN(price)) {
      return false;
    }
    if (!priceFilter) {
      return true;
    }
    if (priceFilter.endsWith("+")) {
      const minimum = Number(
        priceFilter.replace("+", "")
      );
      return price >= minimum;
    }
    const parts = priceFilter.split("-");
    if (parts.length === 2) {
      const minimum = Number(parts[0]);
      const maximum = Number(parts[1]);
      return (
        price >= minimum &&
        price <= maximum
      );
    }
    return false;
  };

  /* ============================
     FILTER PRODUCTS
  ============================ */

const filteredProducts = useMemo(() => {
  const keyword = search.trim().toLowerCase();
  return products
    .map((item) => {
      /* ============================
         SEARCH
      ============================ */
      if (keyword) {
        const design =
          item?.design?.toString().toLowerCase() || "";
        const shade =
          item?.shade?.toString().toLowerCase() || "";
        const designShade =
          item?.designshade?.toString().toLowerCase() || "";
        const matched =
          design.includes(keyword) ||
          shade.includes(keyword) ||
          designShade.includes(keyword);
        if (!matched) {
          return null;
        }
      }

      /* ============================
         DESIGN FILTER
      ============================ */

      if (
        selectedFilters.design.length > 0 &&
        !selectedFilters.design.includes(item?.design)
      ) {
        return null;
      }

      /* ============================
         SHADE FILTER
      ============================ */

      if (
        selectedFilters.shade.length > 0 &&
        !selectedFilters.shade.includes(item?.shade)
      ) {
        return null;
      }

      /* ============================
         ORIGINAL SIZES
      ============================ */

      const sizes = Array.isArray(item?.sizes)
        ? item.sizes
        : [];

      /* ============================
         FILTER SIZES
      ============================ */

      const filteredSizes = sizes.filter((sizeItem) => {
        /* SIZE */

        if (selectedFilters.size.length > 0) {
          const sizeMatch =
            selectedFilters.size.includes(
              sizeItem?.size
            );

          if (!sizeMatch) {
            return false;
          }
        }

        /* SHAPE */

        if (selectedFilters.shape.length > 0) {
          const shapeMatch =
            selectedFilters.shape.includes(
              sizeItem?.shape
            );

          if (!shapeMatch) {
            return false;
          }
        }

        /* PRICE */

        if (selectedFilters.price) {
          const priceMatch = priceMatches(
            sizeItem?.mrp,
            selectedFilters.price
          );

          if (!priceMatch) {
            return false;
          }
        }

        return true;
      });

      /* ============================
         CHECK SIZE/SHAPE/PRICE FILTER
      ============================ */

      const isSizeFilterApplied =
        selectedFilters.size.length > 0;

      const isShapeFilterApplied =
        selectedFilters.shape.length > 0;

      const isPriceFilterApplied =
        Boolean(selectedFilters.price);

      const isSizeLevelFilterApplied =
        isSizeFilterApplied ||
        isShapeFilterApplied ||
        isPriceFilterApplied;

      if (
        isSizeLevelFilterApplied &&
        filteredSizes.length === 0
      ) {
        return null;
      }

      /* ============================
         RETURN PRODUCT
      ============================ */

      return {
        ...item,
        sizes: filteredSizes,
      };
    })
    .filter(Boolean);
}, [
  products,
  search,
  selectedFilters,
]);

  /* ============================
     FILTER SELECTED PRODUCT SIZES
     IMPORTANT
  ============================ */
  const filteredSizes = useMemo(() => {
    if (!selectedProduct?.sizes) {
      return [];
    }
    return selectedProduct.sizes.filter(
      (sizeItem) => {
        /* SIZE FILTER */
        if (selectedFilters.size.length > 0) {
          if (
            !selectedFilters.size.includes(
              sizeItem.size
            )
          ) {
            return false;
          }
        }
        /* SHAPE FILTER */
        if (selectedFilters.shape.length > 0) {
          if (
            !selectedFilters.shape.includes(
              sizeItem.shape
            )
          ) {
            return false;
          }
        }
        /* PRICE FILTER */
        if (selectedFilters.price) {
          if (
            !priceMatches(
              sizeItem.mrp,
              selectedFilters.price
            )
          ) {
            return false;
          }
        }
        return true;
      }
    );
  }, [
    selectedProduct,
    selectedFilters.size,
    selectedFilters.shape,
    selectedFilters.price,
  ]);




  /* ============================
     MOBILE REDIRECT
  ============================ */

  useEffect(() => {
  const checkDevice = () => {
    if (window.innerWidth <= 768 && slug) {
      const source = searchParams.get("source") || 'header';
      const includeComingSoon = Number(searchParams.get("includeComingSoon")) || 0;
      navigate(
        `/mobile/${slug}/list?source=${source}&includeComingSoon=${includeComingSoon}`??`/${slug}/list?source=${source}&includeComingSoon=${includeComingSoon}`,
        { replace: true }
      );    
      }
  };
  window.addEventListener("resize", checkDevice);
  checkDevice();
    return () => window.removeEventListener("resize", checkDevice);
  }, [slug, navigate, searchParams]);




  

  /* ============================
     BREADCRUMB
  ============================ */

  const breadcrumbItems = selectedProduct
    ? [
        {
          name: selectedProduct.category_name,
          path:
            selectedProduct.category_action_link,
        },
        {
          name: selectedProduct.quality_name,
          path: getgridUrl(slug),
        },
      ]
    : [];

  const designShade =
    selectedProduct?.designshade || "";

  return (
    <>
      <SkeletonTheme
        baseColor="#e0e0e0"
        highlightColor="#f5f5f5"
      >
        <Breadcrumb items={breadcrumbItems} />

        {!loading && products.length === 0 ? (
          <div className="container">
            <div
              className="text-center py-5"
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#777",
              }}
            >
              <img
                src={noproduct}
                alt="no product"
                style={{
                  width: "140px",
                  margin: "0 auto",
                }}
              />
              <h4>No products found.</h4>
            </div>
          </div>
        ) : (
          <section className="Product-Detials-box">
            <div className="container">
              <div className="row">
                    {/* GALLERY */}
                <div className="col-md-4 col-md-myyy-4">
                  <GalleryImage
                    loading={loading}
                    mainimage={selectedImage}
                    quality_name={
                      selectedProduct?.quality_name
                    }
                    designShade={designShade}
                    images={
                      selectedProduct?.images || []
                    }
                  />
                </div>

                {/* RIGHT */}

                <div className="col-md-8 col-md-myyy-8">
                  <div className="boxshadow001">
                    {loading ? (
                      <Skeleton
                        height={38}
                        width="100%"
                        borderRadius={6}
                      />
                    ) : (

                      <div className="box-rtoresponcive"> 
                      <div className="row align-items-center"> {/* SHOWING */} 
                        <div className="col-md-5 col-5">
                           <p className="mb-0"> Showing <b>{products.length}</b> Items </p> </div> {/* SEARCH */} <div className="col-md-3 col-3 search-column"> <div className="Filter-Desk"> <SearchFilter search={search} setSearch={setSearch} /> </div> 
                           <div className="Filtermobilesearch"> <SearchFilter search={search} setSearch={setSearch} /> </div> 
                           </div> {/* FILTER + GRID */} 
                           <div className="col-md-4 col-4 filter-column">
                             <div className="d-flex justify-content-end align-items-center"> <MobileFilterPdp products={products} selectedFilters={selectedFilters} onApplyFilters={handleApplyFilters} onClearFilters={handleClearFilters} /> <Link to={getGridActionLink(slug,source, includeComingSoon)} className="text-3xl text-gray-400 hover001" style={{ color: "rgb(187 183 181 / 92%)", marginLeft: "5px", }} title="Go to Grid" > <i className="bi bi-grid-3x3-gap-fill"></i> </Link> </div> 
                        </div> 
                        </div>
                      </div>
                    )}

                    {/* NO FILTER RESULT */}

                    {!loading &&
                      products.length > 0 &&
                      filteredProducts.length === 0 && (
                        <div
                          className="text-center py-4"
                          style={{ color: "#777" }}>
                          <img
                            src={noproduct}
                            alt="No product"
                            style={{
                              width: "110px",
                              margin: "0 auto",
                            }}
                          />
                          <h5 className="mt-2">
                            No products found for selected
                            filters.
                          </h5>
                        </div>
                      )}

                    {/* PRODUCT GRID */}
                    <div className="all-images-box mt-2">
                      <div className="row row-cols-3 row-cols-md-4 row-cols-lg-4 row-cols-xl-4 g-2 text-center">
                        {loading ? (
                          Array.from({
                            length: 10,
                          }).map((_, index) => (
                            <div
                              className="col"
                              key={index}
                            >
                              <Skeleton height={170} />
                            </div>
                          ))
                        ) : (
                          filteredProducts.map(
                            (item, index) => (
                              <div
                                className="col"
                                key={
                                  item.product_id ||
                                  item.product_listing_id ||
                                  `${item.designshade}-${index}`
                                }
                              >
                                <div
                                  className="boximg"
                                  onClick={() =>
                                    handleImageClick(
                                      item
                                    )
                                  }
                                  style={{
                                    cursor: "pointer",
                                    border:
                                      selectedImage ===
                                      item.image_path
                                        ? "1px solid #f8cd05"
                                        : "",
                                    borderRadius: "5px",
                                    padding:
                                      "6px 2px 2px 2px",
                                  }}
                                >
                                  <img
                                    src={
                                      item.image_path
                                    }
                                    alt={
                                      item.design ||
                                      item.designshade ||
                                      "Product"
                                    }
                                    className="img-fluid"
                                  />
                                  <h2 className="heading-h2">
                                    {
                                      item.designshade
                                    }
                                  </h2>
                                </div>
                              </div>
                            )
                          )
                        )}
                      </div>
                    </div>

                    {/* CART TABLE */}
                    <div className="desktop">
                      <CartList
                        sizes={filteredSizes}
                        loading={loading}
                      />
                    </div>
                    {/* DESCRIPTION */}

                    {loading ? (
                      <Skeleton height={30} />
                    ) : (
                      <SpecifIcation
                        description={
                          selectedProduct?.description ||
                          ""
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </SkeletonTheme>
      <CatalogComponent
        products={filteredProducts}
        selectedFilters={selectedFilters}
        search={search}
      />
    </>
  );
}
export default ProductDetails;